import type { PropsWithChildren } from 'react';
import { useRef } from 'react';

import { type Blob, DirectUpload } from '@rails/activestorage';
import type { UploadRequestOption } from '@rc-component/upload/lib/interface';
import type { UploadProps } from 'antd';
import { Upload, App } from 'antd';
import type { RcFile } from 'antd/es/upload';
import cn from 'classnames';
import type { FieldValues, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import { useUpdateAuthDataMutation } from '@/api/endpoints/casdoorAuth';
import { casdoorMutex } from '@/api/queries/casdoorMutex';
import { FieldValidationMessage } from '@/shared/components/FieldValidationMessage/FieldValidationMessage';
import { ENVS, HTTPStatusCode } from '@/shared/consts';
import { getCasdoorAccessToken, getCasdoorRefreshToken, logoutUser, saveCasdoorTokens } from '@/shared/helpers';

interface IProp extends UploadProps {
  acceptFormats: string;
  fieldName: string;
  fileCheckCb: ({
    file,
    fieldName,
    setError,
    clearErrors,
    maxFileSizeInMb,
    fileFormats,
  }: {
    file: RcFile;
    fieldName: string;
    setError: UseFormSetError<FieldValues>;
    clearErrors: UseFormClearErrors<FieldValues>;
    maxFileSizeInMb: number;
    fileFormats: string;
  }) => boolean;
  maxFileSizeInMb?: number;
  shouldBeDisabled?: boolean;
  requestCallback?: (blob: Blob) => void;
  changeCallback?: VoidFunction;
  className?: string;
}

const MAX_RETRIES_COUNT = 2;

export const FileUploadField = ({
  acceptFormats,
  fieldName,
  fileCheckCb,
  maxFileSizeInMb = 10,
  shouldBeDisabled,
  requestCallback,
  changeCallback,
  className,
  children,
  showUploadList = false,
  maxCount = 1,
  multiple = false,
  ...restProps
}: PropsWithChildren<IProp>) => {
  const maxRetriesCount = useRef(MAX_RETRIES_COUNT);

  const { control, setError, clearErrors } = useFormContext();

  const [updateAuthData, { isLoading: isRefetchingToken }] = useUpdateAuthDataMutation();

  const { message } = App.useApp();

  const refreshTokenAndReuploadFile = async (options: UploadRequestOption<unknown>) => {
    if (casdoorMutex.isLocked()) {
      await casdoorMutex.waitForUnlock();
      void handleCustomRequest(options);

      return;
    }

    const release = await casdoorMutex.acquire();

    try {
      const refreshToken = getCasdoorRefreshToken();
      const { access_token: casdoorAccessToken, refresh_token: newCasdoorRefreshToken } = await updateAuthData({
        refreshToken,
      }).unwrap();

      saveCasdoorTokens({ casdoorAccessToken, casdoorRefreshToken: newCasdoorRefreshToken });

      void handleCustomRequest(options);
    } catch {
      logoutUser();
    } finally {
      release();
    }
  };

  const handleCustomRequest = async (options: UploadRequestOption<unknown>) => {
    await casdoorMutex.waitForUnlock();

    const accessToken = getCasdoorAccessToken();

    if (!accessToken) {
      return;
    }

    const { file, onError, onSuccess } = options;

    const directUpload = new DirectUpload(
      file as File,
      `${ENVS.BACKEND_URL}/rails/active_storage/direct_uploads`,
      undefined,
      {
        Authorization: `Bearer ${accessToken}`,
      },
    );

    directUpload.create((error, blob) => {
      if (error) {
        const errMsg = typeof error === 'string' ? error : error.message;
        const statusCode = Number(errMsg.replace(/^.*status:\s?(\d+)$/i, '$1')) as HTTPStatusCode;

        if (statusCode === HTTPStatusCode.UnAuthorized && maxRetriesCount.current > 0) {
          maxRetriesCount.current -= 1;

          void refreshTokenAndReuploadFile(options);

          return;
        }

        onError?.(error);

        message.error('Произошла ошибка при загрузке файла');
      } else {
        maxRetriesCount.current = MAX_RETRIES_COUNT;

        onSuccess?.(blob);

        if (blob) {
          requestCallback?.(blob);
        }
      }
    });
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { value, ...restFieldProps }, fieldState: { invalid: isInvalid, error } }) => (
        <div>
          <Upload.Dragger
            {...restProps}
            {...restFieldProps}
            accept={acceptFormats}
            name={fieldName}
            className={cn(className)}
            beforeUpload={(file) =>
              fileCheckCb({ file, fieldName, setError, clearErrors, maxFileSizeInMb, fileFormats: acceptFormats })
            }
            customRequest={handleCustomRequest}
            disabled={isRefetchingToken || shouldBeDisabled}
            showUploadList={showUploadList}
            maxCount={maxCount}
            multiple={multiple}
          >
            {children}
          </Upload.Dragger>
          {isInvalid && <FieldValidationMessage message={error?.message} />}
        </div>
      )}
    />
  );
};
